import React from 'react';
import { ListItemButton, Box, Typography, styled, alpha } from '@mui/material';
import { ButtonName, RetailerIconGroup } from '@brandclub/common-ui';
import { ShopAppButton } from '@/components/button/ShopAppButton';

const StyledImg = styled('img')<{ maxWidth: number; height?: number }>(({ maxWidth, height }) => ({
  width: '100%',
  maxWidth,
  objectFit: 'contain',
  borderRadius: '0%',
  height,
}));

const CombinedListButton = React.forwardRef(
  (
    {
      variant, // "product" or "store"
      onClick,
      selected,
      mobile,
      // Props for product variant:
      product,
      retailerIds,
      getBrandLogo,
      // Props for store variant:
      store,
      getEntityImage,
      ...otherProps
    }: {
      variant: 'product' | 'store';
      onClick: () => void;
      selected: boolean;
      mobile: boolean;
      product: any;
      retailerIds: any[];
      getBrandLogo: (brandId: string) => string;

      store: any;
      getEntityImage: (id: number | string, type: 'brand' | 'product' | 'retailer') => string;
      otherProps?: any;
    },
    ref,
  ) => {
    const productDisplayName = product?.retailerDisplayName || product?.brandName;
    const productImageSrc = product?.retailerId
      ? getEntityImage(product.retailerId, 'retailer')
      : getBrandLogo(product?.brandId);

    return (
      <ListItemButton
        component="div"
        ref={ref as React.RefObject<HTMLDivElement>}
        disableRipple
        onClick={onClick}
        selected={selected}
        sx={(theme) => ({
          py: mobile ? '14px' : '22.9px',
          pr: mobile ? '18px' : '24.2px',
          pl: mobile ? '18px' : '24.2px',
          marginBottom: '16px',
          borderRadius: '8px',
          border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          bgcolor: 'inherit',
          '&:hover': {
            borderColor: theme.vars.palette.primary.main,
            bgcolor: 'inherit',
          },
          '&.Mui-selected': {
            borderColor: theme.vars.palette.primary.main,
            bgcolor: 'inherit',
            '&:hover': {
              bgcolor: 'inherit',
            },
          },
        })}
        {...otherProps}
      >
        {variant === 'product' && product && (
          <Box display="flex" flexDirection="row" width="100%" gap="19.6px">
            <Box>
              <StyledImg
                alt={`${productDisplayName} logo`}
                src={productImageSrc}
                height={mobile ? 36 : 50}
                maxWidth={mobile ? 36 : 50}
              />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography
                  sx={(theme) => ({
                    fontSize: mobile ? '12px' : '14px',
                    color: theme.vars.palette.text.primary,
                  })}
                >
                  {productDisplayName}
                </Typography>
                <ShopAppButton
                  variant="solid"
                  color="tertiary"
                  height={{ xs: 32, md: 35 }}
                  buttonName={ButtonName.LocationMapSearchResultsButton}
                  sx={(theme) => ({
                    pl: '24.5px',
                    pr: '23.5px',
                    pt: '6.8px',
                    pb: '6.5px',
                    font: mobile ? theme.vars.font.xsButton : theme.vars.font.smButton,
                    letterSpacing: '0.56px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    flexShrink: 0,
                  })}
                >
                  +Add
                </ShopAppButton>
              </Box>
              <Typography
                sx={(theme) => ({
                  fontSize: mobile ? '16px' : '20px',
                  fontWeight: 'bold',
                  color: theme.vars.palette.text.primary,
                  mb: '8.3px',
                })}
              >
                Purchase online
              </Typography>
              <RetailerIconGroup
                retailerIds={retailerIds}
                iconSize={25}
                maxSize={3}
                buildUnrenderedIconCountString={(count: number) => (count > 0 ? `+${count} more` : '')}
              />
            </Box>
          </Box>
        )}
        {variant === 'store' && store && (
          <Box display="flex" flexDirection="row" width="100%" gap="19.6px">
            <Box>
              <StyledImg
                alt={store.storename}
                src={getEntityImage(store.retailerid, 'retailer')}
                height={mobile ? 36 : 50}
                maxWidth={mobile ? 36 : 50}
              />
            </Box>
            <Box
              sx={{
                flex: 1,
                minWidth: 0,
              }}
            >
              <Box display="flex" justifyContent="space-between">
                <Typography
                  sx={(theme) => ({
                    fontSize: mobile ? '12px' : '14px',
                    color: theme.vars.palette.text.primary,
                  })}
                >
                  {store.storename}
                </Typography>
                <ShopAppButton
                  variant="solid"
                  color="tertiary"
                  height={{ xs: 32, md: 35 }}
                  buttonName={ButtonName.LocationMapSearchResultsButton}
                  sx={(theme) => ({
                    pl: '24.5px',
                    pr: '23.5px',
                    pt: '6.8px',
                    pb: '6.5px',
                    font: mobile ? theme.vars.font.xsButton : theme.vars.font.smButton,
                    letterSpacing: '0.56px',
                    fontWeight: 'bold',
                    cursor: 'pointer',
                    flexShrink: 0,
                  })}
                >
                  View
                </ShopAppButton>
              </Box>
              <Typography
                sx={(theme) => ({
                  fontSize: mobile ? '16px' : '20px',
                  fontWeight: 'bold',
                  color: theme.vars.palette.text.primary,
                  mb: '8.3px',
                  mt: '-5px',
                })}
              >
                {store.city}
              </Typography>
              <Typography
                title={`${store.address}, ${store.city}, ${store.state} ${store.zipcode}`}
                sx={(theme) => ({
                  fontSize: mobile ? '12px' : '16px',
                  letterSpacing: '0.16px',
                  color: theme.vars.palette.text.primary,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  width: '100%',
                })}
              >
                {store.address}, {store.city}, {store.state} {store.zipcode}
              </Typography>
            </Box>
          </Box>
        )}
      </ListItemButton>
    );
  },
);

export default CombinedListButton;
